import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const KIND_LABEL = {
  chat: "Chat message",
  popup_option: "Popup option",
  popup_never: "Never show again",
};
const OPTION_LABEL = { tour: "Platform tour", faq: "How does Clinq work?", human: "Analyst support referral" };

export default function AdminDavidInsights() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [volume, setVolume] = useState([]);
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  useEffect(() => {
    api.get("/admin/david-insights/volume?days=30")
      .then(({ data }) => setVolume(data.days || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        if (q) params.set("q", q);
        if (kind) params.set("kind", kind);
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const { data } = await api.get(`/admin/david-insights?${params.toString()}`);
        setRows(data.rows || []);
        setTotal(data.total || 0);
      } catch (_) { /* noop */ }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, kind, dateFrom, dateTo, page]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminShell current="/admin/david-insights">
      <div className="px-6 py-6">
        <h1 className="text-xl font-bold text-white" data-testid="david-insights-title">David Insights</h1>
        <p className="mt-1 text-sm text-neutral-400">Every founder interaction with David — popup choices, questions, answers and dismissals.</p>

        <div className="mt-5 rounded-2xl border border-neutral-800 bg-neutral-900 p-4" data-testid="david-insights-chart">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Daily interaction volume (30 days)</p>
          <div className="mt-3 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={volume}>
                <XAxis dataKey="date" tick={{ fill: "#737373", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                <YAxis tick={{ fill: "#737373", fontSize: 10 }} allowDecimals={false} width={28} />
                <Tooltip contentStyle={{ background: "#171717", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#d4d4d4" }} />
                <Bar dataKey="count" fill="#0A8B85" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <input data-testid="david-insights-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search founder, email or question…"
            className="w-64 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-[#0A8B85]" />
          <select data-testid="david-insights-kind" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none">
            <option value="">All types</option>
            <option value="chat">Chat messages</option>
            <option value="popup_option">Popup options</option>
            <option value="popup_never">Never show again</option>
          </select>
          <input data-testid="david-insights-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none" />
          <span className="text-xs text-neutral-500">to</span>
          <input data-testid="david-insights-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none" />
          <span className="ml-auto text-xs text-neutral-500" data-testid="david-insights-total">{total} interactions</span>
        </div>

        <div className="mt-3 overflow-x-auto rounded-2xl border border-neutral-800">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="david-insights-table">
            <thead className="bg-neutral-900 text-[11px] uppercase tracking-wider text-neutral-400">
              <tr>
                <th className="px-4 py-3">Date / time</th>
                <th className="px-4 py-3">Founder</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Question / detail</th>
                <th className="px-4 py-3">David's response</th>
                <th className="px-4 py-3">Answered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800 bg-neutral-950">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-neutral-500">No interactions match these filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.interaction_id} data-testid={`david-insight-row-${r.interaction_id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-400">
                    {new Date(r.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-neutral-100">{r.founder_name || "—"}</p>
                    <p className="text-xs text-neutral-500">{r.founder_email}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      r.kind === "chat" ? "bg-sky-500/10 text-sky-300"
                        : r.kind === "popup_never" ? "bg-red-500/10 text-red-300"
                        : "bg-violet-500/10 text-violet-300"}`}>
                      {KIND_LABEL[r.kind] || r.kind}
                    </span>
                  </td>
                  <td className="max-w-[280px] px-4 py-3 text-neutral-300">
                    {r.kind === "popup_option" ? (OPTION_LABEL[r.option] || r.option)
                      : r.kind === "popup_never" ? "Clicked 'Do not show this again'"
                      : (r.question || "—")}
                  </td>
                  <td className="max-w-[300px] px-4 py-3 text-neutral-400">
                    {r.answer ? (r.answer.length > 160 ? r.answer.slice(0, 160) + "…" : r.answer) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.kind !== "chat" ? <span className="text-neutral-600">—</span>
                      : r.answered
                        ? <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Yes</span>
                        : <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">No</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
          <button data-testid="david-insights-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-neutral-700 px-3 py-1.5 font-semibold text-neutral-300 disabled:opacity-40">← Prev</button>
          <span>Page {page} of {pages}</span>
          <button data-testid="david-insights-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-neutral-700 px-3 py-1.5 font-semibold text-neutral-300 disabled:opacity-40">Next →</button>
        </div>
      </div>
    </AdminShell>
  );
}
